import type { CollectionsApi } from '@wix/wix-code-collections-api'
import {
  addMainSlugFieldIfNotExists,
  getCollection,
  MAIN_SLUG_PATTERN,
} from '@wix/wix-data-client-common'
import type {
  DynamicPageRouterData,
  EditorSDK,
  Logger,
  RouterConfig,
} from '@wix/wix-data-client-common-standalone'
import { intersection, uniq } from 'lodash'

const getCollectionsUsedInRoutesWithMainSlug = (
  routers: DynamicPageRouterData[],
) => {
  const collections = []

  for (const router of routers) {
    const patterns = Object.entries(router.config.patterns)

    for (const [pattern, patternDef] of patterns) {
      const collectionId = getCollection(patternDef)

      if (pattern === MAIN_SLUG_PATTERN) {
        collections.push(collectionId)
      }
    }
  }

  return uniq(collections)
}

export const addMissingSlugFields = async ({
  editorSdkProxy,
  collectionsApi,
  logger,
}: {
  editorSdkProxy: EditorSDK
  collectionsApi: CollectionsApi
  logger: Logger
}) => {
  try {
    const [schemas, routers] = await Promise.all([
      collectionsApi.getAll(),
      editorSdkProxy.routers.getAll<RouterConfig>(),
    ])

    const allCollectionIds = schemas
      .filter(schema => !schema.isDeleted)
      .map(schema => schema.id)

    const collectionsUsedInRoutes =
      getCollectionsUsedInRoutesWithMainSlug(routers)

    const nonDeletedCollectionsUsedInRoutes = intersection(
      allCollectionIds,
      collectionsUsedInRoutes,
    )

    for (const collectionId of nonDeletedCollectionsUsedInRoutes) {
      try {
        await addMainSlugFieldIfNotExists({ collectionsApi, collectionId })
      } catch (error) {
        logger.error(
          new Error(
            `Failed to add missing slug field to collection ${collectionId}.`,
            {
              cause: error,
            },
          ),
        )
      }
    }
  } catch (cause) {
    logger.error(new Error(`Failed to add missing slug fields`, { cause }))
  }
}
